// src/components/common/PhoneInput.tsx

import React, { useState, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { formatPhoneNumber, removePhoneFormatting, isValidBrazilianPhone, getPhonePlaceholder } from '../../utils/phoneFormatter';

interface PhoneInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onValidationChange,
  error,
  helperText,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(false);

  // Atualiza o valor exibido quando o valor externo muda
  useEffect(() => {
    if (value !== undefined) {
      const formatted = formatPhoneNumber(value);
      setDisplayValue(formatted);
      
      const valid = value.length > 0 ? isValidBrazilianPhone(formatted) : true;
      setIsValid(valid);
      
      if (onValidationChange) {
        onValidationChange(valid);
      }
    }
  }, [value, onValidationChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const formatted = formatPhoneNumber(inputValue);
    const digitsOnly = removePhoneFormatting(inputValue);
    
    setDisplayValue(formatted);
    
    // Valida apenas se há conteúdo
    const valid = digitsOnly.length === 0 || isValidBrazilianPhone(formatted);
    setIsValid(valid);
    
    if (onValidationChange) {
      onValidationChange(valid);
    }
    
    // Retorna apenas os dígitos para o formik
    onChange(digitsOnly);
  };

  const dynamicPlaceholder = getPhonePlaceholder(displayValue);
  
  // Determina se deve mostrar erro
  const showError = error || (displayValue.length > 0 && !isValid);
  const errorMessage = error 
    ? helperText 
    : (displayValue.length > 0 && !isValid) 
      ? 'Formato de telefone inválido' 
      : helperText;

  return (
    <TextField
      {...props}
      value={displayValue}
      onChange={handleChange}
      error={Boolean(showError)}
      helperText={errorMessage}
      placeholder={dynamicPlaceholder}
      inputProps={{
        maxLength: 15, // (11) 99999-9999
        inputMode: 'tel',
        ...props.inputProps,
      }}
    />
  );
};

export default PhoneInput;